<script setup lang="ts">
import { inject, ref, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import capitalize from 'lodash/capitalize';
import { guideList, tourTypeList } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { required } from '@vuelidate/validators';
import { useToast } from 'primevue/usetoast';
import { useViewComposable } from '@/views/composable';
import { useVuelidate } from '@vuelidate/core';
import { useTourManagementStore } from '@/store/TourManagementStore';
import Button from 'primevue/button';
import Card from 'primevue/card';
import InputText from 'primevue/inputtext';
import InputTextAera from 'primevue/textarea';
import Timeline from 'primevue/timeline';
import LoadingSpinner from '@/components/LoadingSpinner.vue';

const dialogRef = inject('dialogRef') as Ref;
const data = dialogRef?.value?.options.data.data;
const groupTypeList = dialogRef?.value?.options.data.groupTypeList;

const { t } = useI18n({ useScope: 'global' });
const currentLanguage = getCurrentLocale();
const langCapitalize = capitalize(currentLanguage);
const toast = useToast();
const tourManagementStore = useTourManagementStore();
const { apiOrder } = useViewComposable();
const { renderLocalTimeFromUtc } = useViewComposable();

const isLimit = ref(true);
const isAddNote = ref(false);
const isShowError = ref(false);
const isLoading = ref(false);
const noteList = ref([]);
const newNote = ref({
  title: '',
  content: ''
});
const rules = {
  title: {
    required
  },
  content: {
    required
  }
};

const v$ = useVuelidate(rules, newNote.value);

const columns = [
  { field: 'svId', header: t('management.tour.list_tour_detail.sv_id') },
  { field: 'ttsId', header: t('management.tour.list_tour_detail.tts_id') },
  { field: 'tourNameEn', header: t('management.tour.tour_name_en') },
  { field: 'tourNameFr', header: t('management.tour.tour_name_fr') },
  { field: 'tourInclusionEn', header: t('management.tour.tour_inclusions_en') },
  { field: 'tourInclusionFr', header: t('management.tour.tour_inclusions_en') },
  { field: 'tourDurationEn', header: t('management.tour.tour_duration_en') },
  { field: 'tourDurationFr', header: t('management.tour.tour_duration_fr') },
  { field: 'tourType', header: t('management.tour.list_tour_detail.tour_type') },
  { field: 'guided', header: t('management.tour.list_tour_detail.guided') },
  { field: 'visitedCitiesEn', header: t('management.tour.visited_cities_en') },
  { field: 'visitedCitiesFr', header: t('management.tour.visited_cities_fr') },
  { field: 'imageLink', header: t('management.tour.list_tour_detail.image_link') },
  { field: 'tourLinkEn', header: t('management.tour.tour_details_link_en') },
  { field: 'tourLinkFr', header: t('management.tour.tour_details_link_fr') },
  { field: 'tourDescriptionEn', header: t('management.tour.tour_description_en') },
  { field: 'tourDescriptionFr', header: t('management.tour.tour_description_fr') },
  { field: 'destinationCode', header: t('management.tour.list_tour_detail.destination_code') },
  { field: 'maxRooms', header: t('management.tour.list_tour_detail.max_rooms') },
  { field: 'quoteDate', header: t('management.tour.list_tour_detail.quote_dates') },
  { field: 'travelDate', header: t('management.tour.list_tour_detail.travel_dates') },
  { field: 'blackoutDate', header: t('management.tour.list_tour_detail.blackout_dates') },
  { field: 'groupTypes', header: t('management.tour.list_tour_detail.group_types') },
  { field: 'tourApiCallOrder', header: t('management.tour.list_tour_detail.tour_api_call_order') },
  { field: 'cacheType', header: t('management.tour.list_tour_detail.cache_type') },
  { field: 'instaQuote', header: t('management.tour.list_tour_detail.insta_quote') },
  { field: 'status', header: t('management.tour.list_tour_detail.status') },
  { field: 'notes', header: t('management.tour.list_tour_detail.notes') },
  { field: 'deleteStatus', header: t('management.tour.deleted_status') },
  { field: 'action', header: t('management.tour.list_tour_detail.action') }
];

const showFullNote = () => {
  isLimit.value = false;
};

const onCancelNote = () => {
  isAddNote.value = false;
  isShowError.value = false;
  Object.assign(newNote.value, initEmptyNote());
};

const initEmptyNote = () => {
  return {
    title: '',
    content: ''
  };
};

const getNotes = () => {
  isLoading.value = true;
  const payload = {
    pk: data.pk,
    sk: data.sk
  };
  tourManagementStore
    .getNotes(payload)
    .then((res) => {
      noteList.value = res.data;
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};
getNotes();

const addNote = () => {
  isShowError.value = true;
  v$.value.$validate();
  if (!v$.value.$error) {
    isLoading.value = true;
    const payload = {
      action: 'NOTE',
      title: newNote.value.title,
      content: newNote.value.content,
      ttsId: data.ttsId,
      svId: data.svId
    };
    tourManagementStore
      .addNote(payload)
      .then(() => {
        isAddNote.value = false;
        getNotes();
        Object.assign(newNote.value, initEmptyNote());
        isShowError.value = false;
        toast.add({ severity: 'success', summary: '', detail: t('notification.submit_success'), life: 3000 });
      })
      .catch(() => {
        toast.add({ severity: 'error', summary: '', detail: t('notification.submit_error'), life: 5000 });
      })
      .finally(() => {
        isLoading.value = false;
      });
  }
};

const generateHeader = (logNotes: any) => {
  return logNotes
    .map((item: any) => {
      const fieldObj = columns.find((columnsItem: any) => columnsItem.field === item.fieldName);
      const result = {
        field: item.fieldName,
        textData: `${fieldObj?.header}: ${handleFormatValue(item.oldValue, item.fieldName)} -> ${handleFormatValue(item.newValue, item.fieldName)}`
      };
      return fieldObj ? result : null;
    })
    .filter((header: any) => !!header);
};

const handleFormatValue = (value: string, field: string) => {
  if (field === 'tourType' || field === 'guided') {
    return handleReplaceValueToName(value, field);
  }
  if (field === 'groupTypes') {
    const groupTypes = getListExistGroupType(value, groupTypeList);
    return convertListGroupType(groupTypes, groupTypeList);
  }
  if (['quoteDate', 'blackoutDate', 'travelDate'].includes(field)) {
    const startMatch = value.match(/start:\s*([\d-:T]+)/);
    const endMatch = value.match(/end:\s*([\d-:T]+)/);
    const dateFormat = [
      {
        start: startMatch ? startMatch[1] : null,
        end: endMatch ? endMatch[1] : null
      }
    ];
    return handleDisplayDate(dateFormat);
  }

  if (field === 'tourApiCallOrder') {
    return getOrderLabel(apiOrder.value, value);
  }
  return value;
};

const handleReplaceValueToName = (value: string, field: string) => {
  if (field === 'tourType') {
    return tourTypeList.find((tourType: any) => tourType.value === value)?.[`name${langCapitalize}`];
  }
  return guideList.find((guide: any) => guide.value === value)?.[`name${langCapitalize}`];
};

const getListExistGroupType = (data: any, groupTypes: any) => {
  const dataSubstring = data?.replace('[', '');
  const dataResult = dataSubstring?.replace(']', '');
  const dataArray = dataResult?.split(', ');
  return dataArray?.filter((group: any) => groupTypes?.map((groupType: any) => groupType.sk).includes(group));
};

const handleDisplayDate = (listDate: any, separator = '\n') => {
  let format = '';
  if (listDate.length) {
    format = listDate.map((item: any) => `${formatDate(item.start, 'dd-LLL-yyyy', true, currentLanguage)} to ${formatDate(item.end, 'dd-LLL-yyyy', true, currentLanguage)}`).join(separator);
  }
  return format;
};

const getOrderLabel = (apiOrder: any, slotPropsValue: any) => {
  return apiOrder.find((type: any) => type.value === slotPropsValue)?.label;
};

const isLinkText = (field: string) => {
  return ['imageLink', 'tourLinkEn', 'tourLinkFr'].includes(field);
};

const convertListGroupType = (groupTypes: any, groupTypeList: any) => {
  const configList = groupTypeList?.filter((item: any) => groupTypes?.includes(item.sk));
  return configList?.reduce((prev: any, cur: any) => {
    if (!prev) {
      return getTextVariant(currentLanguage, cur.name, cur.nameFr);
    }
    return prev + ', ' + getTextVariant(currentLanguage, cur.name, cur.nameFr);
  }, '');
};
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <p>
    {{ t('management.tour.list_tour_detail.note_title') }} {{ t('management.tour.list_tour_detail.tts_id') }}: <b>{{ data.ttsId }}</b> - {{ t('management.tour.list_tour_detail.sv_id') }}: <b>{{ data.svId }}</b>
  </p>
  <div class="card mb-2">
    <Timeline :value="isLimit && noteList?.length > 5 ? noteList.slice(0, 5) : noteList" align="alternate" class="customized-timeline">
      <template #marker="slotProps">
        <span class="custom-marker acvShadow" :style="{ backgroundColor: slotProps.item.color || '#e7201d' }">
          <i :class="slotProps.item.icon"></i>
        </span>
      </template>
      <template #content="slotProps">
        <Card class="acvShadow">
          <template #title>
            <p>{{ slotProps.item.title }}</p>
          </template>
          <template #subtitle>
            <p>{{ slotProps.item.content ?? slotProps.item[`content${langCapitalize}`] }}</p>
          </template>
          <template #content>
            <div v-for="item in generateHeader(slotProps.item.logs)" :key="item">
              <p :class="{ 'text-link-style': isLinkText(item.field) }">{{ item.textData }}</p>
            </div>
            <p class="text-right font-semibold mb-0">{{ slotProps.item?.createdBy }}</p>
            <p class="text-right font-semibold">{{ formatDate(renderLocalTimeFromUtc(slotProps.item.createdTime), 'dd-LLL-yyyy HH:mm', true, currentLanguage) }}</p>
          </template>
        </Card>
      </template>
    </Timeline>
    <div v-if="isLimit && noteList?.length > 5" class="flex justify-content-center">
      <Button class="text-center cursor-pointer outline-none border-noround my-3" @click="showFullNote" :label="$t('common.buttons.show_more')" />
    </div>
    <div class="flex justify-content-center">
      <Button class="text-center cursor-pointer outline-none border-noround my-3" icon="pi pi-plus" @click="isAddNote = !isAddNote" :label="$t('note.add_note')" />
    </div>
    <div v-if="isAddNote" class="note-footer my-5">
      <div class="flex">
        <div class="w-full">
          <InputText v-model="newNote.title" maxlength="100" class="w-full" :placeholder="$t('note.title_placeholder')" />
          <span v-if="isShowError && v$.title.$invalid" class="text-red-500">
            {{ v$.title?.required?.$message }}
          </span>
        </div>
      </div>
      <div class="flex flex-col flex-1 mt-2">
        <InputTextAera v-model="newNote.content" maxlength="1000" :placeholder="$t('note.content_placeholder')" class="w-full" />
        <span v-if="isShowError && v$.content.$invalid" class="text-red-500">
          {{ v$.content?.required?.$message }}
        </span>
      </div>
      <div class="mt-3">
        <Button class="mr-2" :label="$t('common.buttons.save')" @click="addNote()" />
        <Button id="cancel-id" :label="$t('common.buttons.cancel')" @click="onCancelNote()" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.width-300px {
  width: 300px;
}
.custom-marker {
  display: flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  border-radius: 50%;
  z-index: 1;
}

:deep(.p-timeline-event-content),
:deep(.p-timeline-event-opposite) {
  line-height: 1;
}

:deep(.p-image) {
  img {
    max-height: 200px;
    width: 100%;
  }
}

:deep(.p-card-content) {
  padding: 0;
  white-space: pre-line;
}

:deep(.p-fileupload-buttonbar) {
  justify-content: center;
}

.uploadSignature {
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

@media screen and (max-width: 960px) {
  ::v-deep(.customized-timeline) {
    .p-timeline-event:nth-child(even) {
      flex-direction: row !important;

      .p-timeline-event-content {
        text-align: left !important;
      }
    }

    .p-timeline-event-opposite {
      flex: 0;
    }

    .p-card {
      margin-top: 1rem;
    }
  }
}
:deep(.p-timeline-vertical.p-timeline-alternate .p-timeline-event:nth-child(even) .p-timeline-event-content) {
  text-align: left;
}
.text-link-style {
  max-width: 900px;
  word-wrap: break-word;
  word-break: break-all;
  white-space: normal;
}
</style>
